import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import cn from "classnames";
import { useState, useRef, useMemo, useEffect, useCallback, useId, createContext, useContext, useLayoutEffect, Fragment as Fragment$1, forwardRef } from "react";
import { createPortal } from "react-dom";
import { format, endOfMonth, startOfMonth, eachDayOfInterval, getDay, isSameMonth, getDate, isToday, isSameDay, parseISO, isTomorrow, addMonths, isBefore, isAfter, subMonths, eachMonthOfInterval, endOfDay, startOfDay, eachHourOfInterval } from "date-fns";
import Currency$1 from "currency.js";
import { enGB } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { usePopper } from "react-popper";
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeList } from "react-window";
const bem = (block, element = "", modifiers = {}) => {
  if (typeof element === "object") {
    modifiers = element;
    element = "";
  }
  const elementClassPostfix = element.length > 0 ? `__${element}` : "";
  const blockElementClass = `${block}${elementClassPostfix}`;
  const modifiersObject = Object.entries(modifiers).reduce((mem, [key, value]) => {
    return {
      ...mem,
      [`${blockElementClass}--${key}`]: value
    };
  }, {});
  return cn(blockElementClass, modifiersObject);
};
const Icon = ({ name, size, className, onClick, dataTag, isDecorative = false, ...rest }) => /* @__PURE__ */ jsx(
  "span",
  {
    className: cn("font-icon", `font-icon-${name}`, className, size),
    onClick,
    "data-tag": dataTag || `font-icon-${name}`,
    "aria-hidden": isDecorative,
    ...rest,
    children: /* @__PURE__ */ jsx("span", { className: cn("font-icon__content"), translate: "no", "data-nosnippet": "true", children: name })
  }
);
const Accordion = (props) => {
  const {
    title,
    children,
    divider = true,
    onChange,
    renderIcon,
    className,
    opened,
    titleComponent: TitleComponent = "h4",
    titleClassName,
    variant = "flat",
    renderChildrenWhileClosed = false
  } = props;
  const [open, setOpen] = useState(opened);
  const contentWrapper = useRef(null);
  const content = useRef(void 0);
  const onContentLoaded = (element) => {
    content.current = element;
    if (!contentWrapper.current) return;
    if (element != null) {
      contentWrapper.current.style.minHeight = `${element.offsetHeight}px`;
      observer.observe(content.current);
    } else {
      contentWrapper.current.style.minHeight = "0px";
    }
  };
  const observer = useMemo(
    () => new ResizeObserver(
      (entries) => window.requestAnimationFrame(() => {
        if (!contentWrapper.current) return;
        const height = entries[0].target.offsetHeight;
        contentWrapper.current.style.height = `${height}px`;
        contentWrapper.current.style.minHeight = `${height}px`;
      })
    ),
    []
  );
  useEffect(() => {
    return () => {
      observer.disconnect();
    };
  }, [observer]);
  useEffect(() => {
    const transitionCallback = () => {
      if (!contentWrapper.current) return;
      contentWrapper.current.style.flexBasis = content.current ? "auto" : "0px";
    };
    contentWrapper.current?.addEventListener("transitionend", transitionCallback);
  }, []);
  useEffect(() => {
    if (opened && contentWrapper.current && content.current) {
      contentWrapper.current.style.minHeight = `${content.current.offsetHeight}px`;
    }
  }, []);
  const handleHeaderClick = () => {
    setOpen((open2) => {
      onChange?.(!open2);
      return !open2;
    });
  };
  return /* @__PURE__ */ jsxs("div", { className: cn("column", className, bem("ui-accordion", { [variant]: true })), children: [
    /* @__PURE__ */ jsx("div", { className: "cell ui-accordion__header", onClick: handleHeaderClick, children: /* @__PURE__ */ jsxs("div", { className: "row", children: [
      /* @__PURE__ */ jsx(TitleComponent, { className: cn("cell m-0", titleClassName), children: title }),
      /* @__PURE__ */ jsx("div", { className: "cell no-grow row center", children: renderIcon ? renderIcon(!!open) : /* @__PURE__ */ jsx("div", { className: cn("ui-accordion__expand", { open }), children: /* @__PURE__ */ jsx(Icon, { name: "chevron-down", size: "medium" }) }) })
    ] }) }),
    /* @__PURE__ */ jsxs("div", { className: "cell ui-accordion__content-wrapper", ref: contentWrapper, children: [
      renderChildrenWhileClosed && !open && /* @__PURE__ */ jsx("div", { className: bem("ui-accordion", "content", { closed: true }), children }, "content"),
      open && /* @__PURE__ */ jsx("div", { className: "ui-accordion__content", ref: onContentLoaded, children }, "content")
    ] }),
    divider && variant === "flat" && /* @__PURE__ */ jsx("hr", {})
  ] });
};
const Loader = ({ loading, children, title, width, height, className }) => {
  if (!loading) return children;
  return /* @__PURE__ */ jsxs("div", { className: cn("loader column", className), children: [
    /* @__PURE__ */ jsx("svg", { style: { width, height }, className: "loader__spinner", viewBox: "25 25 50 50", children: /* @__PURE__ */ jsx("circle", { className: "spinner__path", cx: "50", cy: "50", r: "20", fill: "none", strokeWidth: "2", strokeMiterlimit: "10" }) }),
    title && /* @__PURE__ */ jsx("div", { className: "loader__title", children: title })
  ] });
};
const Button = ({
  onClick,
  color = "primary",
  variant = "filled",
  round,
  children,
  disabled: disabledProp,
  buttonType = "button",
  fullWidth = true,
  loading,
  className,
  dataTag,
  tabIndex,
  ...rest
}) => {
  const buttonWrapperClasses = cn("ui-button-wrapper", color, variant, className, { round, fullWidth });
  const disabled = disabledProp || loading;
  const isShadowVisible = variant === "filled" && !disabled && !round;
  return /* @__PURE__ */ jsxs("div", { className: buttonWrapperClasses, ...rest, children: [
    loading && /* @__PURE__ */ jsx(Loader, { loading }),
    isShadowVisible && /* @__PURE__ */ jsx("div", { className: "ui-button-shadow" }),
    /* @__PURE__ */ jsx(
      "button",
      {
        type: buttonType,
        className: "ui-button",
        disabled,
        onClick,
        "data-tag": dataTag,
        tabIndex,
        children
      }
    )
  ] });
};
const Alert = ({ message, onClose, type, variant = "default" }) => {
  const isCompact = variant === "compact";
  const iconMap = {
    error: "cross-circle",
    info: "info"
  };
  return /* @__PURE__ */ jsxs("div", { className: cn("row space-between ui-alert center", type), children: [
    /* @__PURE__ */ jsx("div", { className: "cell", children: /* @__PURE__ */ jsxs("div", { className: cn("row gap-2", { "items-center": !isCompact }), children: [
      /* @__PURE__ */ jsx(
        Icon,
        {
          className: cn("ui-alert__type-icon", { "mt-0.5": isCompact }),
          name: iconMap[type],
          size: isCompact ? (
            /* istanbul ignore next*/
            "small"
          ) : "large"
        }
      ),
      /* @__PURE__ */ jsx("div", { className: "body-14", children: message })
    ] }) }),
    /* @__PURE__ */ jsx("div", { className: "cell no-grow", children: onClose && /* @__PURE__ */ jsx(Button, { className: "ui-alert__close", variant: "text", onClick: onClose, children: /* @__PURE__ */ jsx(Icon, { name: "cross", size: "medium" }) }) })
  ] });
};
const useArrowControls = (items, onSelect, onClose, selectedItem) => {
  const [activeIndex, setActiveIndex] = useState();
  const activeItem = useMemo(() => activeIndex != null ? items[activeIndex] : null, [activeIndex, items]);
  const selectedIndex = useMemo(
    () => selectedItem != null ? items.indexOf(selectedItem) : null,
    [items, selectedItem]
  );
  const resetActiveItem = useCallback(
    (index = selectedIndex) => {
      setActiveIndex(index);
    },
    [selectedIndex]
  );
  useEffect(() => {
    resetActiveItem();
  }, [resetActiveItem]);
  const toPreviousItem = () => {
    const newIndex = activeIndex != null ? activeIndex - 1 : items.length - 1;
    setActiveIndex(newIndex >= 0 ? newIndex : items.length - 1);
  };
  const toNextItem = () => {
    const newIndex = activeIndex != null ? activeIndex + 1 : 0;
    setActiveIndex(newIndex < items.length ? newIndex : 0);
  };
  const selectActiveItem = () => {
    if (activeItem != null) onSelect(activeItem);
  };
  const keyHandlersMap = {
    ArrowUp: toPreviousItem,
    ArrowDown: toNextItem,
    Enter: selectActiveItem,
    Escape: onClose
  };
  const onKeyDown = (e) => {
    if (keyHandlersMap[e.key] != null) {
      e.preventDefault();
      keyHandlersMap[e.key]();
    }
  };
  return { activeItem, onKeyDown, resetActiveItem };
};
const useScreenIsLess = (screenWidth) => {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  return useMemo(() => width <= screenWidth, [width, screenWidth]);
};
const mobileScreenWidth = 768;
const useIsMobile = () => useScreenIsLess(mobileScreenWidth);
const InputLabel = ({ text, required, className, htmlFor }) => {
  return /* @__PURE__ */ jsx("label", { className, htmlFor, "data-nosnippet": "true", children: `${text}${required ? "*" : ""}` });
};
const DATE_LIMIT_VALUE = 10;
const Input = (props) => {
  const {
    className,
    iconBefore,
    iconAfter,
    label,
    disabled,
    errorMessage,
    onFocus,
    onBlur,
    onKeyDown,
    name,
    required,
    type = "text",
    max,
    min,
    formNoValidate,
    maxLength,
    trim = true,
    placeholder,
    multiline,
    onPressEnter,
    resettable,
    autoFocus,
    autoComplete = "off",
    dataTag,
    onChange,
    onClick,
    value,
    slot,
    ariaLabel,
    readOnly,
    inputRef
  } = props;
  const [isFocused, setIsFocused] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const inputSlotRef = useRef(null);
  const uniqueId = useId();
  const inputId = name ? `${name}-${uniqueId}` : uniqueId;
  const labelClassNames = cn("ui-input-label", {
    elevated: isFocused || value || placeholder
  });
  const wrapperClassNames = cn(
    bem("ui-input-wrapper", {
      active: isActive,
      focused: isFocused,
      labeled: label,
      error: errorMessage,
      multiline,
      resettable,
      disabled,
      "icon-before": iconBefore,
      "icon-after": iconAfter
    }),
    props.wrapperClassName
  );
  const applyFocusStyles = useCallback(() => setIsFocused(true), []);
  const handleFocus = (e) => {
    setIsFocused(true);
    onFocus?.(e);
  };
  const resetFocusStyles = useCallback(() => {
    setIsFocused(false);
    setIsActive(false);
  }, []);
  const handleBlur = (e) => {
    resetFocusStyles();
    const value2 = trim ? e.target.value.trim() : e.target.value;
    onChange?.(value2);
    onBlur?.(e, value2);
  };
  const handleKeyDown = (e) => {
    if (type === "date" && e.code === "Space") e.preventDefault();
    setIsActive(true);
    onKeyDown?.(e);
    if (e.key === "Enter") {
      onPressEnter?.(e);
    }
  };
  const handleChange = (e) => {
    if (type === "date" && e.target.value.length > DATE_LIMIT_VALUE) return;
    if (multiline) {
      e.target.style.minHeight = e.target.scrollHeight + "px";
    }
    onChange?.(e.target.value);
  };
  const handleReset = () => {
    onChange?.("");
  };
  const combinedAriaLabel = errorMessage && ariaLabel ? `${errorMessage} ${ariaLabel}` : errorMessage || ariaLabel;
  const inputProps = {
    disabled,
    value,
    className: "ui-input",
    type,
    onChange: handleChange,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onKeyDown: handleKeyDown,
    onClick,
    name,
    id: inputId,
    max,
    min,
    maxLength,
    placeholder: typeof placeholder === "string" ? placeholder : void 0,
    autoFocus,
    autoComplete,
    formNoValidate,
    "data-tag": dataTag,
    "aria-required": required || void 0,
    "aria-label": combinedAriaLabel,
    "aria-invalid": Boolean(errorMessage) || void 0,
    "aria-describedby": errorMessage ? `${inputId}-error` : void 0,
    readOnly
  };
  return /* @__PURE__ */ jsxs("div", { "data-tag": dataTag && `${dataTag}-wrapper`, className, children: [
    /* @__PURE__ */ jsxs("div", { className: wrapperClassNames, onClick: () => inputSlotRef.current?.focus(), children: [
      /* @__PURE__ */ jsx("div", { className: bem("ui-input-wrapper", "icon"), "aria-hidden": true, children: iconBefore }),
      label && /* @__PURE__ */ jsx(InputLabel, { className: labelClassNames, text: label, required, htmlFor: inputId }),
      slot && /* @__PURE__ */ jsx(
        slot.Component,
        {
          ref: inputSlotRef,
          className: inputProps.className,
          id: inputProps.id,
          onBlur: resetFocusStyles,
          onFocus: applyFocusStyles,
          ...slot.props
        }
      ),
      !slot && /* @__PURE__ */ jsx(Fragment, { children: multiline ? /* @__PURE__ */ jsx("textarea", { ...inputProps, rows: 3 }) : /* @__PURE__ */ jsx("input", { ...inputProps, ref: inputRef }) }),
      /* @__PURE__ */ jsx("div", { className: bem("ui-input-wrapper", "icon", { after: true }), "aria-hidden": true, children: iconAfter }),
      resettable && value && !disabled && /* @__PURE__ */ jsx(Icon, { name: "cross", size: "small", onClick: handleReset, className: "ui-input-reset" })
    ] }),
    errorMessage && /* @__PURE__ */ jsxs(
      "div",
      {
        id: `${inputId}-error`,
        role: "status",
        "aria-live": "polite",
        tabIndex: -1,
        className: "row start ui-input-error-message",
        children: [
          /* @__PURE__ */ jsx(Icon, { "aria-hidden": true, name: "alert", size: "small" }),
          /* @__PURE__ */ jsx("span", { children: errorMessage })
        ]
      }
    )
  ] });
};
const PopupContainerContext = createContext({ current: document.body });
const FOCUSABLE_SELECTORS = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])'
];
const Modal = ({
  children,
  fullScreen,
  fullWidth,
  fullHeight,
  header,
  footer,
  opened,
  onClose,
  title,
  className,
  maxWidth = "lg",
  sticky,
  closeAfterTransition,
  dataTag,
  shouldCloseOnOverlayClick = true,
  innerShadows = true,
  labelledbyId = "dialog_label",
  describedbyId = "dialog_desc"
}) => {
  const desktop = !useIsMobile();
  const modalRef = useRef(null);
  const backdropRef = useRef(null);
  const [isModalVisible, setIsModalVisible] = useState(opened);
  const popupContainer = useContext(PopupContainerContext);
  const lastActiveElement = useRef(null);
  const getFocusableElements = useCallback(() => {
    if (!modalRef.current) return [];
    return Array.from(modalRef.current.querySelectorAll(FOCUSABLE_SELECTORS.join(",")));
  }, []);
  const updateBodyStyles = useCallback((isOpened) => {
    document.body.style.paddingRight = isOpened ? window.innerWidth - document.body.clientWidth + "px" : "";
    document.body.style.overflow = isOpened ? "hidden" : "";
    document.body.style.touchAction = isOpened ? "none" : "";
  }, []);
  const focusLastActiveElement = useCallback(() => {
    if (lastActiveElement.current instanceof HTMLElement) lastActiveElement.current?.focus();
  }, []);
  const openModal = useCallback(() => {
    updateBodyStyles(true);
    setIsModalVisible(true);
  }, [updateBodyStyles]);
  const closeModal = useCallback(
    (event) => {
      const hideModal = () => {
        updateBodyStyles(false);
        setIsModalVisible(false);
        focusLastActiveElement();
        if (event && closeAfterTransition) {
          onClose?.(event);
        }
      };
      if (modalRef.current) {
        modalRef.current.classList.replace("ui-modal--opened", "ui-modal--closed");
        modalRef.current.addEventListener("animationend", hideModal, { once: true });
      } else {
        hideModal();
      }
    },
    [closeAfterTransition, focusLastActiveElement, onClose, updateBodyStyles]
  );
  const handleCloseAction = (event) => {
    if (closeAfterTransition) {
      closeModal(event);
    } else {
      onClose?.(event);
    }
  };
  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      focusLastActiveElement();
      onClose?.();
      return;
    }
    if (event.key === "Tab") {
      const elements = getFocusableElements();
      if (!elements.length) return;
      const [first, last] = [elements[0], elements[elements.length - 1]];
      const current = document.activeElement;
      const shouldCycle = !modalRef.current?.contains(current) || event.shiftKey && current === first || !event.shiftKey && current === last;
      if (shouldCycle) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      }
    }
  };
  useLayoutEffect(() => {
    if (opened) {
      lastActiveElement.current = document.activeElement;
      if (modalRef.current?.contains(document.activeElement)) return;
      else modalRef.current?.focus();
    }
  }, [opened]);
  useEffect(() => {
    if (opened) openModal();
    else closeModal();
  }, [opened]);
  useEffect(() => {
    return () => {
      updateBodyStyles(false);
    };
  }, [updateBodyStyles]);
  if (!isModalVisible && !opened) return null;
  const modalClassNames = bem("ui-modal", {
    opened,
    closed: !opened,
    "full-screen": fullScreen,
    "full-width": fullWidth,
    "full-height": fullHeight,
    [maxWidth]: true,
    floating: desktop && !sticky,
    [`sticky-${sticky}`]: sticky,
    "inner-shadows": innerShadows
  });
  const classNames = cn(modalClassNames, className);
  return createPortal(
    /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        "div",
        {
          "data-tag": "modal-overlay",
          ref: backdropRef,
          onClick: shouldCloseOnOverlayClick ? handleCloseAction : void 0,
          className: bem("ui-modal", "overlay")
        }
      ),
      /* @__PURE__ */ jsxs(
        "div",
        {
          "data-tag": dataTag || "modal-window",
          "aria-modal": "true",
          id: "alertdialog",
          role: "alertdialog",
          "aria-labelledby": labelledbyId,
          "aria-describedby": describedbyId,
          ref: modalRef,
          className: classNames,
          tabIndex: 0,
          onKeyDown: handleKeyDown,
          children: [
            title != null && /* @__PURE__ */ jsxs("div", { className: "ui-modal__header", "data-tag": "modal-header", children: [
              /* @__PURE__ */ jsxs("div", { className: "row space-between", children: [
                /* @__PURE__ */ jsx("div", { className: "w-100", children: title }),
                /* @__PURE__ */ jsx("button", { onClick: handleCloseAction, className: "ui-modal__close-button", children: /* @__PURE__ */ jsx(Icon, { name: "cross", size: "large", "data-tag": "modal-close" }) })
              ] }),
              header
            ] }),
            title == null && onClose != null && /* @__PURE__ */ jsx("button", { onClick: handleCloseAction, className: "ui-modal__close-button ui-modal__close-button--floating", children: /* @__PURE__ */ jsx(Icon, { name: "cross", size: "large", "data-tag": "modal-close" }) }),
            /* @__PURE__ */ jsx("div", { "data-tag": "modal-body", className: "ui-modal__content", children }),
            footer && /* @__PURE__ */ jsx("div", { "data-tag": "modal-footer", className: "ui-modal__footer", children: footer })
          ]
        }
      )
    ] }),
    popupContainer.current ?? /* istanbul ignore next: not reachable */
    document.body
  );
};
const Option = ({
  value,
  onSelect,
  checkedMark,
  selected,
  offset,
  active,
  children,
  className,
  separated,
  scroll = true,
  focus = true,
  disabled,
  ...rest
}) => {
  const optionRef = useRef(null);
  const optionClasses = useMemo(
    () => bem("ui-option", { selected, active, separated, disabled }),
    [active, selected, separated, disabled]
  );
  const onOptionClick = (event) => {
    if (disabled) return;
    onSelect(value, event);
  };
  useEffect(() => {
    if (selected && scroll && focus) {
      optionRef.current?.scrollIntoView({ block: "nearest" });
    }
  }, [scroll]);
  useEffect(() => {
    if (active && scroll && focus) {
      requestAnimationFrame(() => {
        optionRef.current?.scrollIntoView({ block: "nearest" });
      });
    }
  }, [active, scroll, focus]);
  return /* @__PURE__ */ jsxs(
    "li",
    {
      role: "option",
      "aria-disabled": disabled,
      style: { paddingLeft: offset },
      ref: optionRef,
      className: cn(className, optionClasses, "row", "space-between"),
      onClick: onOptionClick,
      ...rest,
      children: [
        /* @__PURE__ */ jsx("div", { className: bem("ui-option", "label", { shorten: checkedMark }), children }),
        checkedMark && selected && /* @__PURE__ */ jsx("div", { className: bem("ui-option", "checkmark"), children: /* @__PURE__ */ jsx(Icon, { name: "checkout", size: "medium" }) })
      ]
    }
  );
};
const Options = ({ options, listClassNames, errorMessage }) => {
  return /* @__PURE__ */ jsxs("div", { className: listClassNames, "data-tag": "autocomplete-options", children: [
    options,
    errorMessage && /* @__PURE__ */ jsx("div", { className: "ui-autocomplete__error-message", children: errorMessage })
  ] });
};
const getValue = (value) => {
  if (Array.isArray(value)) return value.at(-1) ?? null;
  return value;
};
const checkSelected = (value, selected) => {
  if (Array.isArray(value)) return value.includes(selected);
  return value === selected;
};
const Autocomplete = ({
  value,
  inputValue,
  options,
  renderOption,
  getOptionChildren,
  onInputChange,
  onSelect,
  onInputFocus,
  onInputBlur,
  inputErrorMessage,
  emptyOptionMessage,
  inputDisabled,
  config,
  renderListTitle,
  dataTag,
  inputSlot
}) => {
  const [opened, setOpened] = useState(false);
  const id = useId();
  const scroll = useRef(0);
  const isMobile = useIsMobile();
  const allOptions = useMemo(
    () => options.flatMap((item) => [item, ...getOptionChildren(item)]),
    [getOptionChildren, options]
  );
  const handleScroll = useCallback((top) => {
    window.scrollTo({ behavior: "instant", top });
  }, []);
  const handleToggle = useCallback(
    (state) => {
      setOpened(state);
      if (!isMobile) return;
      if (state) {
        scroll.current = window.scrollY;
        handleScroll(0);
      } else {
        handleScroll(scroll.current);
      }
    },
    [handleScroll, isMobile]
  );
  const handleSelect = (option, event) => {
    event?.stopPropagation();
    const position = allOptions.indexOf(option) + 1;
    onSelect(option, position);
    handleToggle(false);
  };
  const { onKeyDown, activeItem, resetActiveItem } = useArrowControls(
    allOptions,
    handleSelect,
    () => handleToggle(false),
    getValue(value)
  );
  const handleKeyDown = (e) => {
    if (e.key === "Tab") setOpened(false);
    onKeyDown(e);
  };
  const renderOptions = (list, level = 0) => {
    return list.map((option, index) => /* @__PURE__ */ jsxs(Fragment$1, { children: [
      renderListTitle?.(option),
      /* @__PURE__ */ jsx(
        Option,
        {
          value: option,
          onSelect: handleSelect,
          selected: checkSelected(value, option),
          active: option === activeItem,
          offset: level ? level * 45 : 16,
          "data-tag": "autocomplete-option",
          checkedMark: true,
          children: renderOption(option)
        }
      ),
      renderOptions(getOptionChildren(option), level + 1)
    ] }, String(id) + String(index)));
  };
  const listClassNames = bem("ui-autocomplete", "list", { opened });
  useEffect(() => {
    if (!opened) {
      resetActiveItem();
    }
  }, [opened, resetActiveItem]);
  const getInput = (props) => /* @__PURE__ */ jsx(
    Input,
    {
      value: inputValue,
      onChange: onInputChange,
      errorMessage: inputErrorMessage,
      onFocus: onInputFocus,
      onBlur: onInputBlur,
      disabled: inputDisabled,
      onKeyDown: handleKeyDown,
      trim: false,
      resettable: true,
      dataTag,
      slot: inputSlot ? { ...inputSlot, props: { ...inputSlot.props, onKeyDown: handleKeyDown } } : void 0,
      ariaLabel: String(props?.label),
      ...props
    }
  );
  const renderedOptions = /* @__PURE__ */ jsx(Options, { options: renderOptions(options), listClassNames, errorMessage: emptyOptionMessage });
  if (isMobile) {
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("div", { className: "ui-autocomplete", onClick: () => handleToggle(true), children: /* @__PURE__ */ jsx("div", { className: "ui-autocomplete__main-input", children: getInput({ ...config?.default }) }) }),
      /* @__PURE__ */ jsx(
        Modal,
        {
          opened,
          className: bem("ui-autocomplete__modal", { opened }),
          fullScreen: true,
          header: getInput({ ...config?.modal, label: null, autoFocus: true }),
          title: config?.modal?.label,
          onClose: (e) => {
            e?.stopPropagation();
            handleToggle(false);
          },
          children: renderedOptions
        }
      )
    ] });
  }
  return /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsx(
    Tooltip,
    {
      action: "focus",
      content: renderedOptions,
      disabled: isMobile,
      position: "bottom-start",
      opened,
      onOpenChanged: setOpened,
      anchorWidth: true,
      maxHeight: "md",
      maxWidth: "md",
      popperClassName: bem("ui-autocomplete", "tooltip"),
      children: /* @__PURE__ */ jsx("div", { className: "ui-autocomplete", children: getInput({ ...config?.default }) })
    }
  ) });
};
const ButtonGroup = (props) => {
  const { children, flat } = props;
  return /* @__PURE__ */ jsx("div", { className: bem("ui-button-group", { flat }), children });
};
const SettingsContext = createContext({
  dateLocale: enGB,
  currency: {
    getSymbol: (code) => code,
    create: (amount, code) => new Currency$1(amount, { symbol: code })
  },
  time: {
    textInputEnabled: false
  },
  date: {
    textInputEnabled: false
  }
});
const useSettings = () => useContext(SettingsContext);
const useCurrency = () => {
  const { currency } = useSettings();
  return currency;
};
const Item$2 = ({ width, height, className }) => /* @__PURE__ */ jsx("div", { className: cn("ui-skeleton", className), style: { width, height } });
const Divider = ({ className }) => {
  return /* @__PURE__ */ jsx("div", { className: cn("ui-divider", className), "aria-hidden": true });
};
const List = (props) => {
  const { amount, loading, children, Skeleton: Skeleton2, direction = "row", divider } = props;
  if (!loading) return typeof children === "function" ? children() : children || null;
  return /* @__PURE__ */ jsx("div", { className: `${direction} gap-3`, children: new Array(amount).fill(true).map((_, index) => /* @__PURE__ */ jsxs(Fragment$1, { children: [
    /* @__PURE__ */ jsx("div", { className: "cell", children: /* @__PURE__ */ jsx(Skeleton2, {}) }),
    divider && index < amount - 1 && /* @__PURE__ */ jsx("div", { className: "cell", children: /* @__PURE__ */ jsx(Divider, {}) })
  ] }, index)) });
};
const Text = ({ loading, children, placeholder }) => {
  if (!loading) return /* @__PURE__ */ jsx(Fragment, { children: typeof children === "function" ? children() : children });
  return /* @__PURE__ */ jsx("div", { className: "ui-skeleton-text", children: placeholder });
};
const Skeleton = {
  Item: Item$2,
  List,
  Text
};
const DEFAULT_CALENDAR_WEEKS_DAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
const Calendar = ({
  calendarDate,
  selectedDate,
  onDateChange,
  renderHeader,
  isTileDisabled,
  getTileText,
  prices,
  isPriceLoading = false,
  weekDays = DEFAULT_CALENDAR_WEEKS_DAYS
}) => {
  const { dateLocale: locale } = useSettings();
  const currencyUtils = useCurrency();
  const ref = useRef(null);
  const isMobile = useIsMobile();
  const placeholder = currencyUtils.create(760, "EUR").format();
  const monthTitle = useMemo(() => format(calendarDate, "MMMM yyyy", { locale }), [calendarDate, locale]);
  const availableDays = useMemo(() => {
    const interval = { start: startOfMonth(calendarDate), end: endOfMonth(calendarDate) };
    return eachDayOfInterval(interval);
  }, [calendarDate]);
  const previousMonthOffset = useMemo(() => {
    const dayOfWeek = getDay(startOfMonth(calendarDate)) || 7;
    return dayOfWeek - 1;
  }, [calendarDate]);
  const emptyTiles = useMemo(
    () => [...Array(previousMonthOffset).keys()].map((key) => /* @__PURE__ */ jsx("div", { className: bem("tile", { disabled: true }) }, key)),
    [previousMonthOffset]
  );
  const getTileClasses = (date) => {
    const today = isToday(date);
    const selected = selectedDate != null ? isSameDay(date, selectedDate) : false;
    return bem("tile", { selected, today });
  };
  useEffect(() => {
    const date = selectedDate ?? /* @__PURE__ */ new Date();
    if (isSameMonth(date, calendarDate) && isMobile) {
      ref.current?.scrollIntoView();
    }
  }, []);
  const getPrice = (date) => prices?.[format(date, "yyyy-MM-dd", { locale })] ?? null;
  const isBetween = (price, range) => price >= range[0] && price < range[1];
  const formatPrice = (price) => {
    const currency = currencyUtils.create(price.fractional, price.currency);
    const value = currency.dollars();
    const divider = 1e3;
    if (isBetween(value, [0, 1])) return currency.format();
    if (isBetween(value, [1, 1e3])) return value;
    if (isBetween(value, [1e3, 1e4])) return (value / divider).toFixed(1).concat("k");
    return (value / divider).toFixed(0).concat("k");
  };
  const formatDateLabel = (date) => {
    return format(date, "d MMMM yyyy", { locale });
  };
  return /* @__PURE__ */ jsxs("div", { className: bem("ui-calendar"), ref, children: [
    /* @__PURE__ */ jsx("div", { className: bem("ui-calendar", "header"), children: renderHeader != null ? renderHeader(calendarDate, monthTitle) : monthTitle }),
    /* @__PURE__ */ jsxs("div", { className: bem("ui-calendar", "tiles"), children: [
      weekDays.map((day) => /* @__PURE__ */ jsx("div", { className: bem("ui-calendar", "week-day"), children: day }, day)),
      emptyTiles,
      availableDays.map((date) => {
        const price = getPrice(date);
        return /* @__PURE__ */ jsxs(
          "button",
          {
            type: "button",
            className: getTileClasses(date),
            onClick: () => {
              onDateChange(date);
            },
            disabled: isTileDisabled?.(date),
            children: [
              /* @__PURE__ */ jsx("div", { className: bem("tile", "day"), "aria-label": formatDateLabel(date), children: getDate(date) }),
              /* @__PURE__ */ jsx("div", { className: bem("tile", "text"), children: getTileText?.(date) }),
              /* @__PURE__ */ jsx(Skeleton.Text, { placeholder, loading: isPriceLoading, children: price && /* @__PURE__ */ jsxs("div", { className: bem("tile", "price"), children: [
                /* @__PURE__ */ jsx("span", { className: bem("tile", "price-symbol"), children: currencyUtils.getSymbol(price.currency) }),
                /* @__PURE__ */ jsx("span", { children: formatPrice(price) })
              ] }) })
            ]
          },
          date.getTime()
        );
      })
    ] })
  ] });
};
const Card = ({
  opened,
  header,
  body,
  footer,
  details,
  rightSide,
  leftSide,
  disabled,
  disabledLabel,
  dataTag,
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsxs("div", { ...props, className: cn(bem("ui-card", { opened, disabled }), className), "data-tag": dataTag, children: [
    /* @__PURE__ */ jsxs("div", { className: bem("ui-card", "main", { opened, sided: rightSide }), children: [
      leftSide,
      /* @__PURE__ */ jsxs("div", { className: bem("ui-card", "main-content"), children: [
        Boolean(header) && /* @__PURE__ */ jsx("div", { className: bem("ui-card", "header"), children: header }),
        Boolean(body) && /* @__PURE__ */ jsx("div", { className: bem("ui-card", "body"), children: body }),
        Boolean(footer) && /* @__PURE__ */ jsx("div", { className: bem("ui-card", "footer"), children: footer })
      ] }),
      rightSide
    ] }),
    disabled && /* @__PURE__ */ jsx("div", { className: bem("ui-card__overlay", { disabled }), children: /* @__PURE__ */ jsx("div", { children: disabledLabel }) }),
    details
  ] });
};
function sliceMiddle(items, length, middleItemIndex) {
  const previousItemsCount = Math.floor(length / 2);
  const safeStartIndex = Math.max(middleItemIndex - previousItemsCount, 0);
  const sliceStartIndex = Math.min(safeStartIndex, items.length - length);
  const sliceEndIndex = sliceStartIndex + length;
  return items.slice(sliceStartIndex, sliceEndIndex);
}
const Carousel = ({
  renderItem,
  itemsConfig,
  selectedItemIndex,
  viewportLength,
  onChange
}) => {
  const length = Math.min(viewportLength, itemsConfig.length);
  const itemsForRender = sliceMiddle(itemsConfig, length, selectedItemIndex);
  const onClick = (itemConfig, index) => {
    if (index < selectedItemIndex) {
      onChange(itemConfig, "back");
    } else if (index > selectedItemIndex) {
      onChange(itemConfig, "forward");
    }
  };
  return /* @__PURE__ */ jsx("div", { className: "carousel", children: /* @__PURE__ */ jsx("div", { className: "carousel__list", children: itemsForRender.map((config) => {
    const absoluteIndex = itemsConfig.indexOf(config);
    return renderItem(config, absoluteIndex === selectedItemIndex, () => {
      onClick(config, absoluteIndex);
    });
  }) }) });
};
const Item$1 = ({ itemConfig, isSelected, onChange }) => {
  const { isLoading, title, description } = itemConfig;
  const classNames = bem("list-item", { selected: isSelected });
  const onClick = () => {
    if (!isLoading) onChange();
  };
  return /* @__PURE__ */ jsx("button", { className: classNames, onClick, "data-tag": "connections-carousel-item", children: /* @__PURE__ */ jsxs(Loader, { loading: isLoading, children: [
    /* @__PURE__ */ jsx("div", { className: bem("list-item__primary-text"), children: title(itemConfig.id) }),
    description && /* @__PURE__ */ jsx("div", { className: bem("list-item__secondary-text"), children: description(itemConfig.id) })
  ] }) });
};
const Checkbox = ({
  children,
  onChange,
  checked,
  disabled,
  error,
  errorMessage,
  onBlur,
  dataTag,
  ariaLabel
}) => /* @__PURE__ */ jsxs("div", { children: [
  /* @__PURE__ */ jsxs("label", { className: bem("ui-checkbox", { disabled, error, checked }), "data-tag": dataTag, children: [
    /* @__PURE__ */ jsx(
      "input",
      {
        className: "ui-checkbox__input",
        type: "checkbox",
        onChange,
        disabled,
        checked,
        onBlur,
        "aria-label": ariaLabel,
        "data-tag": dataTag
      }
    ),
    /* @__PURE__ */ jsx("span", { className: "ui-checkbox__label", children })
  ] }),
  error && errorMessage && /* @__PURE__ */ jsxs("div", { className: "row start ui-input-error-message", children: [
    /* @__PURE__ */ jsx(Icon, { name: "alert", size: "small" }),
    /* @__PURE__ */ jsx("span", { children: errorMessage })
  ] })
] });
const CheckboxGroup = ({
  value,
  options,
  onChange,
  disabled,
  direction = "column",
  className,
  dataTag
}) => {
  const [groupValue, setGroupValue] = useState(value);
  useEffect(() => {
    setGroupValue(value);
  }, [value, setGroupValue]);
  const onValueChange = useCallback(
    (inputValue) => {
      const newValue = !groupValue.includes(inputValue) ? groupValue.concat([inputValue]) : groupValue.filter((value2) => value2 !== inputValue);
      setGroupValue(newValue);
      onChange?.(newValue);
    },
    [groupValue, setGroupValue, onChange]
  );
  return /* @__PURE__ */ jsx("div", { className: cn(`ui-checkbox-group`, direction, className), "data-tag": dataTag, children: options.map((option) => /* @__PURE__ */ jsx(
    Checkbox,
    {
      checked: groupValue.includes(option.value),
      onChange: () => {
        onValueChange(option.value);
      },
      disabled: disabled ?? option.disabled,
      ariaLabel: String(option.label),
      dataTag: `checkbox-${option.value}`,
      children: option.label
    },
    option.value.toString()
  )) });
};
const keys = {
  Enter: "Enter",
  Escape: "Escape"
};
const onKeyDownEventHandler = (event, callback) => {
  switch (event.key) {
    case keys.Enter:
      event.preventDefault();
      callback?.();
      break;
    case keys.Escape:
      event.preventDefault();
      callback?.();
      break;
  }
};
const useDevice = () => {
  const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isOldIPad = /iPad/.test(userAgent);
  const platform = typeof navigator !== "undefined" ? navigator.platform : "";
  const maxTouchPoints = typeof navigator !== "undefined" ? navigator.maxTouchPoints : 0;
  const isNewIPad = platform === "MacIntel" && maxTouchPoints > 1;
  const isIPad = isOldIPad || isNewIPad;
  const isIOS = /iPhone|iPod/.test(userAgent) || isIPad;
  return { isIOS };
};
function useReactiveState(initialStateFactory, dependencies) {
  const prevDependencies = useRef(dependencies);
  const [internalState, setInternalState] = useState(initialStateFactory);
  if (dependencies.some((dependency, index) => !Object.is(dependency, prevDependencies.current[index]))) {
    setInternalState(initialStateFactory);
    prevDependencies.current = dependencies;
  }
  return [internalState, setInternalState];
}
const useDisableNativeDatepickerPopup = () => {
  const datepickerRef = useRef(null);
  useEffect(() => {
    const el = datepickerRef.current;
    if (!el) return;
    const handler = function(e) {
      e.preventDefault();
      this.blur();
    };
    el.addEventListener("focus", handler);
    return () => {
      el.removeEventListener("focus", handler);
    };
  }, []);
  return { datepickerRef };
};
const preventDefault = (e) => e.preventDefault();
const CalendarIcon = () => /* @__PURE__ */ jsx("div", { className: "ui-date-picker__icon", children: /* @__PURE__ */ jsx(Icon, { name: "calendar", size: "large" }) });
const DatePickerFakeInput = (props) => {
  const { label, withIcon = true, errorMessage, value, active, required, inputLabel } = props;
  const { dateLocale: locale } = useSettings();
  const { t } = useTranslation();
  const getFormatedDate = (value2) => {
    if (isToday(value2)) return t("ui.datePicker.today");
    if (isTomorrow(value2)) return t("ui.datePicker.tomorrow");
    return format(value2, "E, d MMM", { locale });
  };
  const inputValue = inputLabel ?? (value && getFormatedDate(value));
  const inputWrapperClasses = bem("ui-date-picker", "input-wrapper", {
    active,
    error: !!errorMessage
  });
  return /* @__PURE__ */ jsxs("div", { className: inputWrapperClasses, "data-tag": "date-picker-input", tabIndex: 0, children: [
    withIcon && /* @__PURE__ */ jsx(CalendarIcon, {}),
    /* @__PURE__ */ jsxs("div", { className: "ui-date-picker__value", children: [
      /* @__PURE__ */ jsx(
        InputLabel,
        {
          className: bem("ui-date-picker", "label", { focused: inputValue }),
          text: label,
          required
        }
      ),
      /* @__PURE__ */ jsx("span", { children: inputValue })
    ] })
  ] });
};
const DatePickerRealInput = (props) => {
  const { label, withIcon = true, errorMessage, value, active, required, onChange } = props;
  const { dateLocale: locale } = useSettings();
  const [currentValue, setCurrentValue] = useReactiveState(
    () => value ? format(value, "yyyy-MM-dd", { locale }) : void 0,
    [locale, value]
  );
  const handleChange = useCallback(
    (value2) => {
      setCurrentValue(value2);
      const parsed = parseISO(value2);
      if (parsed && !Number.isNaN(parsed.getTime())) onChange(parsed);
    },
    [onChange, setCurrentValue]
  );
  const { isIOS } = useDevice();
  const isMobile = useIsMobile();
  const { datepickerRef } = useDisableNativeDatepickerPopup();
  return /* @__PURE__ */ jsx(
    Input,
    {
      wrapperClassName: bem("ui-date-picker", "input-wrapper", { active, withInput: true, error: !!errorMessage }),
      type: "date",
      label,
      required,
      value: currentValue,
      onBlur: preventDefault,
      onClick: preventDefault,
      onChange: handleChange,
      iconBefore: withIcon && /* @__PURE__ */ jsx(CalendarIcon, {}),
      ariaLabel: label,
      dataTag: "date-picker-input",
      readOnly: isIOS || isMobile,
      inputRef: datepickerRef
    }
  );
};
const DatePickerInput = (props) => {
  const {
    date: { textInputEnabled }
  } = useSettings();
  if (!textInputEnabled || props.inputLabel) {
    return /* @__PURE__ */ jsx(DatePickerFakeInput, { ...props });
  }
  return /* @__PURE__ */ jsx(DatePickerRealInput, { ...props });
};
const DatePickerMenuDesktop = ({
  initialValue,
  value,
  onChange,
  onClose,
  minDate,
  maxDate,
  opened,
  weekDays,
  ...calendarProps
}) => {
  const popupRef = useRef(null);
  const [calendarDate, setCalendarDate] = useState(/* @__PURE__ */ new Date());
  useEffect(() => {
    setCalendarDate(value ?? initialValue ?? /* @__PURE__ */ new Date());
  }, [value, initialValue, setCalendarDate]);
  useEffect(() => {
    if (opened) {
      setTimeout(() => {
        const selectedTile = document.querySelector("button.tile--selected");
        if (selectedTile) {
          selectedTile.focus();
        }
      }, 0);
    }
  }, [opened]);
  const nextPage = () => {
    setCalendarDate((date) => addMonths(date, 2));
  };
  const previousPage = () => {
    setCalendarDate((date) => subMonths(date, 2));
  };
  const onDateSelect = (date) => {
    onChange(date);
    onClose();
  };
  const renderCalendarHeader = (date, title, isPrevious) => {
    const isNavigationDisabled = isPrevious ? isBefore(startOfMonth(date), minDate) : isAfter(endOfMonth(date), maxDate);
    const onClick = isPrevious ? previousPage : nextPage;
    return /* @__PURE__ */ jsxs("div", { className: "ui-date-picker-popup__calendar-header", children: [
      /* @__PURE__ */ jsx("div", { className: cn("ui-date-picker-popup__navigation-button", { right: !isPrevious }), children: /* @__PURE__ */ jsx(Button, { variant: "outline", round: true, onClick, disabled: isNavigationDisabled, children: /* @__PURE__ */ jsx(Icon, { name: isPrevious ? "chevron-left" : "chevron-right", size: "large" }) }) }),
      title
    ] });
  };
  return /* @__PURE__ */ jsxs("div", { ref: popupRef, className: "ui-date-picker-popup", children: [
    /* @__PURE__ */ jsx(
      Calendar,
      {
        calendarDate,
        selectedDate: value,
        onDateChange: onDateSelect,
        renderHeader: (...props) => renderCalendarHeader(...props, true),
        weekDays,
        ...calendarProps
      }
    ),
    /* @__PURE__ */ jsx("div", { className: "ui-date-picker-popup__divider" }),
    /* @__PURE__ */ jsx(
      Calendar,
      {
        calendarDate: addMonths(calendarDate, 1),
        selectedDate: value,
        onDateChange: onDateSelect,
        renderHeader: renderCalendarHeader,
        weekDays,
        ...calendarProps
      }
    )
  ] });
};
const DatePickerMenuMobile = ({
  title,
  value,
  onChange,
  minDate,
  maxDate,
  opened,
  onClose,
  weekDays,
  ...calendarProps
}) => {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState(value);
  useEffect(() => {
    setSelectedDate(value);
  }, [value]);
  const submitDate = useCallback(
    (date) => {
      onChange(date);
      onClose();
    },
    [onChange, onClose]
  );
  const footer = useMemo(
    () => selectedDate && /* @__PURE__ */ jsx("div", { className: "ui-date-picker-modal__footer", children: /* @__PURE__ */ jsx(
      Button,
      {
        onClick: () => {
          submitDate(selectedDate);
        },
        children: t("ui.datePicker.select")
      }
    ) }),
    [selectedDate, submitDate, t]
  );
  return /* @__PURE__ */ jsx(Modal, { className: "ui-date-picker-modal", opened, onClose, title, footer, fullScreen: true, children: /* @__PURE__ */ jsx("div", { className: "ui-date-picker-modal__content", children: eachMonthOfInterval({ start: minDate, end: maxDate }).map((date) => /* @__PURE__ */ jsx(
    Calendar,
    {
      calendarDate: date,
      selectedDate,
      onDateChange: setSelectedDate,
      weekDays,
      ...calendarProps
    },
    date.getTime()
  )) }) });
};
const DatePicker = ({ value, onChange, errorMessage, label, ...calendarProps }) => {
  const wrapperRef = useRef(null);
  const [isCalendarOpened, setIsCalendarOpened] = useState(false);
  const isMobile = useIsMobile();
  const toggleCalendar = () => {
    setIsCalendarOpened((prevState) => !prevState);
    calendarProps.onToggle?.(!isCalendarOpened);
  };
  const inputProps = {
    errorMessage,
    value,
    label,
    active: isCalendarOpened,
    onChange,
    ...calendarProps
  };
  const menuProps = {
    opened: isCalendarOpened,
    onClose: () => {
      setIsCalendarOpened(false);
    },
    value,
    title: label,
    onChange,
    ...calendarProps
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: "ui-date-picker",
      ref: wrapperRef,
      role: "button",
      onKeyDown: (event) => onKeyDownEventHandler(event, () => setIsCalendarOpened(true)),
      children: [
        !isMobile && /* @__PURE__ */ jsx(
          Tooltip,
          {
            popperClassName: "ui-date-picker__tooltip",
            action: "click",
            content: /* @__PURE__ */ jsx(DatePickerMenuDesktop, { ...menuProps }),
            position: calendarProps.calendarPlacement ?? "bottom-start",
            maxWidth: "none",
            maxHeight: "none",
            onOpenChanged: (isOpened) => {
              setIsCalendarOpened(isOpened);
              calendarProps.onToggle?.(isOpened);
            },
            opened: isCalendarOpened,
            children: /* @__PURE__ */ jsx(DatePickerInput, { ...inputProps })
          }
        ),
        isMobile && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx("div", { onClick: toggleCalendar, children: /* @__PURE__ */ jsx(DatePickerInput, { ...inputProps }) }),
          /* @__PURE__ */ jsx(DatePickerMenuMobile, { ...menuProps })
        ] }),
        errorMessage && /* @__PURE__ */ jsxs("div", { className: "ui-date-picker__error-message row", role: "alert", tabIndex: 0, children: [
          /* @__PURE__ */ jsx(Icon, { "aria-hidden": "true", name: "alert", size: "small" }),
          /* @__PURE__ */ jsx("span", { children: errorMessage })
        ] })
      ]
    }
  );
};
const useClickOutside = (elementRef, callback) => {
  const handleClickOutside = useCallback(
    (event) => {
      const target = event.composedPath()[0];
      const elements = Array.isArray(elementRef) ? elementRef : [elementRef];
      const inside = elements.some(({ current }) => current?.contains(target)) ?? false;
      if (!inside) callback();
    },
    [callback, elementRef]
  );
  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [callback, handleClickOutside]);
};
const Popper = forwardRef((props, ref) => {
  const {
    children,
    opened,
    anchorElement,
    onRendered,
    position = "bottom-start",
    anchorWidth,
    className,
    maxWidth = "sm",
    maxHeight = "sm",
    fullWidth,
    ...divProps
  } = props;
  const [popperElement, setPopperElement] = useState(null);
  const resizeObserverRef = useRef(null);
  const popperProps = usePopper(anchorElement, popperElement, {
    placement: position,
    modifiers: [{ name: "offset", options: { offset: [0, 4] } }]
  });
  const { styles, attributes, state, update } = popperProps;
  const popperAlignment = state?.placement.split("-")[0] ?? "bottom";
  const container = useContext(PopupContainerContext);
  useEffect(() => {
    if (popperElement) {
      resizeObserverRef.current = new ResizeObserver(() => {
        requestAnimationFrame(() => update?.());
      });
      resizeObserverRef.current.observe(popperElement);
      return () => {
        resizeObserverRef.current?.disconnect();
      };
    }
  }, [popperElement, update]);
  const handleElementRender = useCallback(
    (element) => {
      if (element) {
        const scrollY = window.scrollY;
        setPopperElement(element);
        onRendered?.(element);
        if (ref) {
          if (typeof ref === "function") {
            ref(element);
          } else {
            ref.current = element;
          }
        }
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollY);
        });
      }
    },
    [onRendered, ref]
  );
  if (!opened) return null;
  const minWidth = anchorWidth ? anchorElement?.offsetWidth : void 0;
  const popperClasses = bem("ui-popper", {
    [`max-w-${maxWidth}`]: true,
    [`max-h-${maxHeight}`]: true,
    "full-width": fullWidth
  });
  return createPortal(
    /* @__PURE__ */ jsx(
      "div",
      {
        "data-tag": "popper",
        ref: handleElementRender,
        className: cn(popperClasses, className, { [popperAlignment]: true }),
        style: { ...styles.popper, minWidth },
        ...attributes.popper,
        ...divProps,
        children
      }
    ),
    container.current
  );
});
Popper.displayName = "Popper";
function debounce(callback, delay) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = window.setTimeout(() => {
      callback(...args);
    }, delay);
  };
}
const ITEM_HEIGHT = 48;
const SearchableList = ({ items, renderItem, value }) => {
  const listRef = useRef(void 0);
  const searchQuery = useRef(null);
  const clearSearchDebounced = useMemo(
    () => debounce(() => {
      searchQuery.current = null;
    }, 500),
    []
  );
  const appendSearchValue = useCallback(
    (text) => {
      searchQuery.current = (searchQuery.current ?? "") + text;
      clearSearchDebounced();
    },
    [clearSearchDebounced]
  );
  const findItemIndex = useCallback(
    (searchQuery2) => {
      const regex = new RegExp(`^${searchQuery2}`, "i");
      return items.findIndex(({ searchValue, value: value2 }) => regex.test(searchValue ?? value2));
    },
    [items]
  );
  const handleKeyPress = useCallback(
    (e) => {
      appendSearchValue(e.key);
      const index = findItemIndex(searchQuery.current);
      if (index >= 0) {
        listRef.current?.scrollToItem(index, "start");
      }
    },
    [appendSearchValue, findItemIndex]
  );
  useEffect(() => {
    document.addEventListener("keypress", handleKeyPress);
    return () => {
      document.removeEventListener("keypress", handleKeyPress);
    };
  }, [handleKeyPress]);
  const onListMount = useCallback(
    (ref) => {
      listRef.current = ref;
      const index = items.findIndex((item) => item.value === value);
      listRef.current?.scrollToItem(index, "center");
    },
    [items, value]
  );
  return /* @__PURE__ */ jsx("div", { className: "ui-searchable-list", children: /* @__PURE__ */ jsx(AutoSizer, { children: ({ width, height }) => /* @__PURE__ */ jsx(
    FixedSizeList,
    {
      ref: onListMount,
      itemSize: ITEM_HEIGHT,
      itemCount: items.length,
      height,
      width,
      children: ({ index, style }) => /* @__PURE__ */ jsx("div", { style, children: renderItem(items[index]) })
    }
  ) }) });
};
const PINNED_LABEL_VALUE = "@@pinnedLabel";
const DropdownList = ({
  items,
  value,
  checkedMark,
  onChange,
  onReset,
  activeItem,
  searchable,
  resettable,
  focus,
  pinnedValues = [],
  pinnedLabels = []
}) => {
  const pinnedItems = useMemo(
    () => pinnedValues.map((value2) => items.find((item) => item.value === value2)).filter((item) => item != null),
    [items, pinnedValues]
  );
  const allItems = useMemo(() => {
    const restItems = items.filter(({ value: value2 }) => !pinnedValues?.includes(value2));
    const [pinnedStartLabel, pinnedEndLabel] = pinnedLabels.map(
      (value2) => pinnedItems.length > 0 && restItems.length > 0 ? {
        label: value2,
        value: PINNED_LABEL_VALUE
      } : null
    );
    return [pinnedStartLabel, ...pinnedItems, pinnedEndLabel, ...restItems].filter(
      (item) => item != null
    );
  }, [items, pinnedItems, pinnedValues, pinnedLabels]);
  const isLastPinnedItem = useCallback(
    (value2) => value2 === pinnedItems[pinnedItems.length - 1]?.value,
    [pinnedItems]
  );
  const defaultOption = useCallback(
    ({ value: itemValue, label, getClass, disabled }) => /* @__PURE__ */ jsx(
      Option,
      {
        value: itemValue,
        onSelect: onChange,
        checkedMark,
        selected: value === itemValue,
        active: activeItem === itemValue,
        className: getClass?.({ selected: activeItem === itemValue }),
        scroll: !searchable,
        separated: pinnedItems.length && !pinnedLabels.length ? isLastPinnedItem(itemValue) : false,
        focus,
        disabled,
        children: label ?? itemValue
      },
      itemValue + crypto.randomUUID()
    ),
    [activeItem, checkedMark, focus, isLastPinnedItem, onChange, pinnedItems.length, pinnedLabels, searchable, value]
  );
  const pinnedLabelOption = useCallback(
    (label) => /* @__PURE__ */ jsx("div", { onClick: (event) => event.stopPropagation(), className: "ui-dropdown__pinned-label", children: label }, label),
    []
  );
  const renderOption = useCallback(
    (item) => {
      if (item.value === PINNED_LABEL_VALUE) return pinnedLabelOption(item.label);
      return defaultOption(item);
    },
    [defaultOption, pinnedLabelOption]
  );
  const renderResetOption = useCallback(
    ({ label }) => /* @__PURE__ */ jsx(
      Option,
      {
        value: "",
        onSelect: (_, event) => {
          onReset?.(event);
        },
        checkedMark,
        children: label
      },
      "no-value"
    ),
    [checkedMark, onReset]
  );
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    resettable && value != null && renderResetOption({ label: " – " }),
    searchable ? /* @__PURE__ */ jsx(SearchableList, { value, items: allItems, renderItem: renderOption }) : allItems.map(renderOption)
  ] });
};
const DropdownMenu = ({
  opened,
  onClose,
  onChange,
  onReset,
  placeholder,
  value,
  activeItem,
  anchorElement,
  popperProps,
  modalClassName,
  header: Header,
  ...listProps
}, ref) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [modalValue, setModalValue] = useState(null);
  const reset = (event) => {
    onReset?.(event);
    setModalValue(null);
    confirmModal();
    onClose();
  };
  const closeModal = () => {
    setModalValue(null);
    onClose();
  };
  const confirmModal = () => {
    if (modalValue) onChange(modalValue);
    closeModal();
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    isMobile && /* @__PURE__ */ jsx(
      Modal,
      {
        className: cn(bem("ui-dropdown", "modal"), modalClassName),
        opened,
        onClose,
        title: placeholder,
        fullScreen: true,
        footer: modalValue && /* @__PURE__ */ jsx(Button, { onClick: confirmModal, children: t("ui.dropdown.confirm") }),
        children: /* @__PURE__ */ jsxs("div", { className: "column cell grow", "data-tag": "dropdown-menu", children: [
          Header ? /* @__PURE__ */ jsx(Header, {}) : null,
          /* @__PURE__ */ jsx("ul", { id: listProps.listboxId, role: "listbox", className: "ui-dropdown__list", "data-tag": "dropdown-menu", children: /* @__PURE__ */ jsx(DropdownList, { onChange: setModalValue, onReset: reset, value: modalValue ?? value, ...listProps }) })
        ] })
      }
    ),
    !isMobile && /* @__PURE__ */ jsx(
      Popper,
      {
        ref,
        opened,
        anchorElement,
        anchorWidth: true,
        maxWidth: "none",
        ...popperProps,
        className: cn("p-0", popperProps?.className),
        children: /* @__PURE__ */ jsxs("div", { className: "ui-dropdown__list-wrapper", children: [
          Header ? /* @__PURE__ */ jsx(Header, {}) : null,
          /* @__PURE__ */ jsx("ul", { id: listProps.listboxId, role: "listbox", className: "ui-dropdown__list", "data-tag": "dropdown-menu", children: /* @__PURE__ */ jsx(DropdownList, { onChange, onReset: reset, value, activeItem, ...listProps }) })
        ] })
      }
    )
  ] });
};
const DropdownMenu$1 = forwardRef(DropdownMenu);
const checkOpeningKey = (event) => event.key === "Enter" || event.code === "Space" || event.key === "ArrowDown";
const Dropdown = ({
  disabled,
  className,
  errorMessage,
  required,
  popperProps,
  dataTag,
  icon,
  header,
  onChange,
  ...restProps
}) => {
  const { value, items, label, onReset } = restProps;
  const [opened, setOpened] = useState(false);
  const close = () => {
    setOpened(false);
  };
  const ref = useRef(null);
  const isMobile = useIsMobile();
  const menuRef = useRef(null);
  const defaultListboxId = useId();
  const listboxId = restProps.listboxId ?? defaultListboxId;
  useClickOutside([ref, menuRef], () => {
    if (!isMobile) setOpened(false);
  });
  const selectValue = (item, event) => {
    const { shouldClose = true } = onChange(item, event) ?? {};
    if (shouldClose) {
      setOpened(false);
    }
  };
  const selectedItem = items.find(({ value: itemValue }) => value === itemValue);
  const { activeItem, onKeyDown, resetActiveItem } = useArrowControls(
    restProps.items,
    (item) => selectValue(item.value),
    close,
    selectedItem
  );
  const toggleOpened = () => {
    setOpened(!opened);
    resetActiveItem();
  };
  const handleKeyDown = (event) => {
    if (event.key === "Backspace") onReset?.();
    if (opened) {
      onKeyDown(event);
      return;
    }
    if (checkOpeningKey(event)) setOpened(true);
  };
  const currentItem = items.find((item) => item.value === value);
  const dropdownBem = bem("ui-dropdown", { disabled });
  const dropdownClassNames = cn(dropdownBem, className);
  const dropdownLabelClassNames = bem("ui-dropdown", "label", { outlined: opened, error: errorMessage });
  const placeholderTextClassNames = bem("placeholder", "text", {
    empty: !value,
    closed: !opened,
    opened
  });
  const focusOnDropdown = () => {
    ref.current?.focus();
  };
  const handleBlur = (event) => {
    if (isMobile) return;
    if (event.relatedTarget && !menuRef.current?.contains(event.relatedTarget)) {
      setOpened(false);
    }
  };
  const getDropdownAriaValue = (text) => `${label ?? value}-${text}`.toLowerCase().replace(/\s+/g, "-");
  useEffect(() => {
    if (!opened) resetActiveItem();
  }, [opened, resetActiveItem]);
  useEffect(() => {
    if (opened && selectedItem) {
      const timeoutId = setTimeout(() => {
        resetActiveItem();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [opened, selectedItem, resetActiveItem]);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      "data-tag": dataTag,
      className: dropdownClassNames,
      ref,
      onClick: focusOnDropdown,
      onKeyDown: handleKeyDown,
      onBlur: handleBlur,
      role: "combobox",
      "aria-labelledby": `${getDropdownAriaValue("dropdown-label")} ${getDropdownAriaValue("dropdown-value")}`,
      "aria-haspopup": "listbox",
      "aria-expanded": opened,
      "aria-controls": listboxId,
      children: [
        /* @__PURE__ */ jsxs(
          "div",
          {
            "aria-labelledby": `${getDropdownAriaValue("dropdown-label")} ${getDropdownAriaValue("dropdown-value")}`,
            className: dropdownLabelClassNames,
            onClick: toggleOpened,
            tabIndex: 0,
            role: "button",
            children: [
              icon,
              /* @__PURE__ */ jsxs("div", { className: "label__placeholder", children: [
                label && /* @__PURE__ */ jsx(
                  "div",
                  {
                    id: getDropdownAriaValue("dropdown-label"),
                    className: placeholderTextClassNames,
                    children: `${label}${required ? "*" : ""}`
                  }
                ),
                /* @__PURE__ */ jsx("div", { id: getDropdownAriaValue("dropdown-value"), className: "placeholder__value", children: currentItem?.selectedLabel ?? currentItem?.label ?? currentItem?.value ?? " " })
              ] }),
              /* @__PURE__ */ jsx(Icon, { "aria-hidden": "true", name: opened ? "chevron-up" : "chevron-down", size: "small" })
            ]
          }
        ),
        errorMessage && /* @__PURE__ */ jsxs("div", { className: "row start ui-dropdown__error-message", children: [
          /* @__PURE__ */ jsx(Icon, { name: "alert", size: "small" }),
          /* @__PURE__ */ jsx("span", { children: errorMessage })
        ] }),
        /* @__PURE__ */ jsx(
          DropdownMenu$1,
          {
            ...restProps,
            header,
            popperProps,
            anchorElement: ref.current,
            placeholder: label,
            activeItem: activeItem?.value,
            onClose: close,
            onChange: selectValue,
            opened,
            ref: menuRef,
            listboxId
          }
        )
      ]
    }
  );
};
const ErrorMessage = ({ children, className }) => {
  return /* @__PURE__ */ jsx("div", { className: cn("message", className), children: /* @__PURE__ */ jsx("p", { className: bem("message", "text"), children }) });
};
const AttachedFile = ({ file, onRemove }) => {
  return /* @__PURE__ */ jsxs("div", { className: "row gap-2", children: [
    /* @__PURE__ */ jsx("div", { className: "cell no-grow", children: /* @__PURE__ */ jsx(Icon, { name: "attachment", size: "large" }) }),
    /* @__PURE__ */ jsx("div", { className: "cell ui-file-upload__file-name", children: file.name }),
    /* @__PURE__ */ jsx(Icon, { className: "ui-file-upload__remove", name: "cross", size: "large", onClick: onRemove })
  ] });
};
const getExtension = (file) => {
  const parts = file.name.split(".");
  return `.${parts[parts.length - 1]}`;
};
const FileUpload = (props) => {
  const { name, maxFiles, maxFileSize, fileTypes, value, onChange } = props;
  const { t } = useTranslation();
  const [invalidFiles, setInvalidFiles] = useState([]);
  const formats = fileTypes.join(", ").toUpperCase();
  const id = `ui-file-upload-${name}-${Math.random()}`;
  const filesizeInBytes = maxFileSize * 1024 * 1024;
  const handleFileInput = (e) => {
    const files = e.target.files;
    if (files) {
      const [validFiles, invalidFiles2] = Array.from(files).reduce(
        ([validFiles2, invalidFiles3], file) => {
          if (file.size > filesizeInBytes || !fileTypes.includes(getExtension(file))) {
            invalidFiles3.push(file);
          } else {
            validFiles2.push(file);
          }
          return [validFiles2, invalidFiles3];
        },
        [[], []]
      );
      onChange([...value, ...validFiles]);
      setInvalidFiles((prev) => [...prev, ...invalidFiles2]);
    }
  };
  const disabled = value.length >= maxFiles;
  return /* @__PURE__ */ jsxs("div", { className: cn("column", bem("ui-file-upload", { disabled })), children: [
    /* @__PURE__ */ jsx("h4", { className: "cell mb-1", children: t("components.fileUpload.title") }),
    /* @__PURE__ */ jsx("div", { className: "cell mb-3 body-14", children: t("components.fileUpload.description", { maxFiles, maxFileSize, formats }) }),
    /* @__PURE__ */ jsx("div", { className: "cell mb-3", children: /* @__PURE__ */ jsxs("div", { className: "column gap-2", children: [
      value.map((file, index) => /* @__PURE__ */ jsx("div", { className: "cell", children: /* @__PURE__ */ jsx(AttachedFile, { file, onRemove: () => onChange(value.filter((_, i) => i !== index)) }) }, file.name)),
      invalidFiles.map((file, index) => /* @__PURE__ */ jsxs(Fragment$1, { children: [
        /* @__PURE__ */ jsx("div", { className: "cell", children: /* @__PURE__ */ jsx(
          AttachedFile,
          {
            file,
            onRemove: () => setInvalidFiles((files) => files.filter((_, i) => i !== index))
          }
        ) }),
        /* @__PURE__ */ jsx("div", { className: cn("cell", bem("ui-file-upload", "invalid")), children: /* @__PURE__ */ jsxs("div", { className: "row items-center gap-1", children: [
          /* @__PURE__ */ jsx(Icon, { name: "alert", size: "small" }),
          /* @__PURE__ */ jsx("div", { className: "body-12", children: t("components.fileUpload.invalidFile", { maxFileSize, formats }) })
        ] }) })
      ] }, file.name))
    ] }) }),
    /* @__PURE__ */ jsxs("label", { className: bem("ui-file-upload", "button"), htmlFor: id, children: [
      /* @__PURE__ */ jsx(Icon, { name: "upload", size: "large" }),
      /* @__PURE__ */ jsx("div", { className: "bolder", children: t("components.fileUpload.upload") }),
      /* @__PURE__ */ jsx(
        "input",
        {
          accept: fileTypes.join(","),
          id,
          onChange: handleFileInput,
          className: bem("ui-file-upload", "input"),
          type: "file",
          value: "",
          disabled
        }
      )
    ] })
  ] });
};
const Tag = ({ children, className, variant, ...props }) => /* @__PURE__ */ jsx("span", { className: cn(bem("ui-tag", variant ? { [variant]: true } : {}), className), ...props, children });
const SvgIcon = ({ icon: IconComponent, size = "large", color, className }) => {
  return /* @__PURE__ */ jsx("div", { className: cn(bem("svg-icon", { [size]: true, [color]: true }), className), children: /* @__PURE__ */ jsx(IconComponent, {}) });
};
const IconText = ({ label, iconName, className }) => /* @__PURE__ */ jsxs("span", { className: cn("ui-icon-text", "row", className), children: [
  /* @__PURE__ */ jsx(Icon, { name: iconName, size: "medium", className: "pr-2", isDecorative: true }),
  /* @__PURE__ */ jsx("span", { className: bem("ui-icon-text", "label"), children: label })
] });
const LinkButton = ({
  to,
  color = "primary",
  variant = "outline",
  round,
  fullWidth = true,
  openInNewTab,
  children,
  className
}) => {
  const wrapperClass = cn("ui-button-wrapper ui-link-button-wrapper", color, variant, className, {
    round,
    fullWidth
  });
  return /* @__PURE__ */ jsx("div", { className: wrapperClass, children: /* @__PURE__ */ jsx("a", { className: "ui-button ui-link-button", href: to, target: openInNewTab ? "_blank" : "_self", rel: "noreferrer", children }) });
};
function moveIndex(array, index, action) {
  switch (action) {
    case "back":
      return index === 0 ? array.length - 1 : index - 1;
    default:
      return index === array.length - 1 ? 0 : index + 1;
  }
}
const getYoutubeId = (url) => {
  const regex = /(?:youtube\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  return url.match(regex)?.[1];
};
const validateVideoUrl = (url) => {
  const regex = /\bhttps?:\/\/\S+\.(mp4|avi|mov|wmv|flv|mkv|webm)\b/;
  return regex.test(url);
};
const Item = ({ url, className, config }) => {
  const renderMedia = useCallback(
    (url2, className2) => {
      const youtubeVideoId = getYoutubeId(url2);
      const previewPostfix = "#t=0.1";
      if (validateVideoUrl(url2)) {
        return /* @__PURE__ */ jsx(
          "video",
          {
            "data-tag": "activeMedia",
            className: className2,
            src: url2.concat(previewPostfix),
            controls: true,
            ...config?.video
          },
          url2
        );
      }
      if (youtubeVideoId) {
        return /* @__PURE__ */ jsx(
          "iframe",
          {
            "data-tag": "activeMedia",
            className: className2,
            src: `https://www.youtube.com/embed/${youtubeVideoId}`,
            allow: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture",
            referrerPolicy: "strict-origin-when-cross-origin",
            allowFullScreen: true,
            ...config?.iframe
          },
          url2
        );
      }
      return /* @__PURE__ */ jsx("img", { "data-tag": "activeMedia", className: className2, src: url2, alt: "", ...config?.image }, url2);
    },
    [config]
  );
  return /* @__PURE__ */ jsx(Fragment, { children: renderMedia(url, className) });
};
const MediaCarouselDesktop = ({ media, className, onMediaChange }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [animationDirection, setAnimationDirection] = useState(null);
  const changeIndex = (index) => {
    setCurrentIndex(index);
    onMediaChange?.(index);
  };
  const goForward = () => {
    setAnimationDirection("forward");
  };
  const goBack = () => {
    setAnimationDirection("back");
  };
  const focusedMediaIndex = useMemo(() => {
    return animationDirection != null ? moveIndex(media, currentIndex, animationDirection) : null;
  }, [animationDirection, media, currentIndex]);
  const handleAnimationEnd = () => {
    changeIndex(focusedMediaIndex ?? /* istanbul ignore next: not possible */
    0);
    setAnimationDirection(null);
  };
  const collection = useCallback(
    (className2) => media.map((url) => /* @__PURE__ */ jsx(Item, { url, className: className2 }, url)),
    [media]
  );
  return /* @__PURE__ */ jsxs("div", { className: cn("column", className, bem("media-carousel", { desktop: true })), children: [
    /* @__PURE__ */ jsxs("div", { className: bem("media-carousel", "media"), children: [
      /* @__PURE__ */ jsxs(
        "div",
        {
          className: bem("media-carousel", "media-container", {
            [animationDirection]: animationDirection != null
          }),
          onAnimationEnd: handleAnimationEnd,
          onTransitionEnd: handleAnimationEnd,
          children: [
            collection()[currentIndex],
            focusedMediaIndex != null && collection(bem("media-carousel", "next-media"))[focusedMediaIndex]
          ]
        }
      ),
      /* @__PURE__ */ jsxs("div", { className: bem("media-carousel", "navigate-arrows", { animating: animationDirection != null }), children: [
        /* @__PURE__ */ jsx(Button, { variant: "outline", round: true, onClick: () => goBack(), children: /* @__PURE__ */ jsx(Icon, { name: "chevron-left", size: "medium" }) }),
        /* @__PURE__ */ jsx(Button, { variant: "outline", round: true, onClick: () => goForward(), children: /* @__PURE__ */ jsx(Icon, { name: "chevron-right", size: "medium" }) })
      ] })
    ] }),
    /* @__PURE__ */ jsx("div", { className: bem("media-carousel", "navigate-dots"), children: media.map((url, index) => /* @__PURE__ */ jsx(
      "div",
      {
        className: bem("media-carousel", "navigate-dot", { active: index === (focusedMediaIndex ?? currentIndex) })
      },
      url
    )) })
  ] });
};
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const NavigateState = (props) => {
  const { currentIndex, media } = props;
  return /* @__PURE__ */ jsx("div", { className: bem("media-carousel", "navigate-dots"), children: media.map((url, index) => /* @__PURE__ */ jsx("div", { className: bem("media-carousel", "navigate-dot", { active: index === currentIndex }) }, url)) });
};
const activeStyles = {
  height: 100,
  opacity: 1
};
const secondaryStyles = {
  height: 85,
  opacity: 0.5
};
const interpolate = (progress, from, to) => from + (to - from) * progress;
const interpolateField = (progress, field) => interpolate(progress, activeStyles[field], secondaryStyles[field]);
const gap = 8;
const mediaSwapLimit = 50;
const MediaCarouselMobile = ({ media, className, onMediaChange }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const mediaRef = useRef(null);
  const dimensionsRef = useRef(new DOMRect());
  const dragStateRef = useRef({ x: 0, dragPosition: 0 });
  const changeIndex = (index) => {
    setCurrentIndex(index);
    onMediaChange?.(index);
  };
  const getStyles = useCallback(
    (progress) => ({
      height: `${interpolateField(progress, "height")}%`,
      opacity: interpolateField(progress, "opacity").toString()
    }),
    []
  );
  const updateMediaStyles = useCallback(
    (element) => {
      const { x, width } = element.getBoundingClientRect();
      const progress = Math.min(Math.abs(x - dimensionsRef.current.x) / width, 1) || 0;
      const { height, opacity } = getStyles(progress);
      element.style.height = height;
      element.style.opacity = opacity;
    },
    [getStyles]
  );
  const updateMedia = useCallback(
    (media2) => {
      for (const child of media2.children) {
        updateMediaStyles(child);
      }
    },
    [updateMediaStyles]
  );
  useEffect(() => {
    const observer = new ResizeObserver(() => {
      const rect = mediaRef.current?.getBoundingClientRect();
      if (mediaRef.current != null && rect != null && rect.height > 0) {
        dimensionsRef.current = rect;
        mediaRef.current.style.height = `${dimensionsRef.current.height}px`;
        updateMedia(mediaRef.current);
        observer.disconnect();
      }
    });
    observer.observe(mediaRef.current);
    return () => observer.disconnect();
  }, [updateMediaStyles, updateMedia]);
  const dragStart = (event) => {
    dragStateRef.current.dragPosition = event.touches[0].clientX;
  };
  const dragMove = (event) => {
    const { x, dragPosition } = dragStateRef.current;
    if (dragPosition != null && mediaRef.current) {
      const { clientX } = event.touches[0];
      const offset = x + clientX - dragPosition;
      const scrollLimit = dimensionsRef.current.width - mediaRef.current.scrollWidth;
      mediaRef.current.style.left = `${clamp(offset, scrollLimit, 0)}px`;
      updateMedia(mediaRef.current);
    }
  };
  const scrollTo = (getDistance, onEnd, duration, startTime = 0) => {
    requestAnimationFrame((delta) => {
      if (startTime !== 0) {
        const progress = (delta - startTime) / duration;
        const distance = getDistance(Math.min(progress, 1) || 0);
        if (mediaRef.current) {
          mediaRef.current.style.left = `${distance}px`;
          updateMedia(mediaRef.current);
        }
        if (progress < 1) {
          scrollTo(getDistance, onEnd, duration, startTime);
        } else {
          dragStateRef.current.x = distance;
          onEnd();
        }
      } else {
        scrollTo(getDistance, onEnd, duration, delta);
      }
    });
  };
  const getOffsetFromIndex = (index) => index * dimensionsRef.current.width + index * gap;
  const focusOnMedia = (index) => {
    const getDistance = (progress) => interpolate(progress, dragStateRef.current.x, -getOffsetFromIndex(index));
    const onEnd = () => changeIndex(index);
    scrollTo(getDistance, onEnd, 250);
  };
  const dragEnd = (event) => {
    if (mediaRef.current && dragStateRef.current.dragPosition != null) {
      const dragDistance = dragStateRef.current.dragPosition - event.changedTouches[0].clientX;
      const indexChange = Math.abs(dragDistance) < mediaSwapLimit ? 0 : Math.sign(dragDistance);
      dragStateRef.current.dragPosition = null;
      dragStateRef.current.x = mediaRef.current.getBoundingClientRect().x - dimensionsRef.current.x;
      focusOnMedia(clamp(currentIndex + indexChange, 0, media.length - 1));
    }
  };
  return /* @__PURE__ */ jsxs("div", { className: cn("column", className, bem("media-carousel", { mobile: true })), children: [
    /* @__PURE__ */ jsx(
      "div",
      {
        className: bem("media-carousel", "media"),
        ref: mediaRef,
        onTouchStart: dragStart,
        onTouchMove: dragMove,
        onTouchEnd: dragEnd,
        children: media.map((url) => /* @__PURE__ */ jsx(Item, { url }, url))
      }
    ),
    /* @__PURE__ */ jsx(NavigateState, { media, currentIndex })
  ] });
};
const MediaCarousel = (props) => {
  const isMobile = useIsMobile();
  return isMobile ? /* @__PURE__ */ jsx(MediaCarouselMobile, { ...props }) : /* @__PURE__ */ jsx(MediaCarouselDesktop, { ...props });
};
const MultiSelect = (props) => {
  const { values, inputValue, onInputChange, onRemove, onKeyDown, className, placeholder, disabled, iconName } = props;
  const wrapperRef = useRef(null);
  const spanRefs = useRef([]);
  const inputRef = useRef(null);
  const hiddenCount = useRef(0);
  const handleKeyDown = (e) => {
    onKeyDown?.(e);
    if (e.key === "Backspace" && !inputValue && values.length) {
      onRemove(values.at(-1)?.value);
    }
  };
  const handleClick = () => {
    inputRef.current?.focus({ preventScroll: true });
  };
  const isOutOfVisibility = useCallback((index) => {
    if (!wrapperRef?.current || !spanRefs?.current[index]) return false;
    const wrapperRect = wrapperRef.current.getBoundingClientRect();
    const spanRect = spanRefs.current[index]?.getBoundingClientRect();
    if (!spanRect) return false;
    return spanRect.x === 0 || spanRect.right > wrapperRect.right;
  }, []);
  useEffect(() => {
    if (!spanRefs.current) return;
    const count = spanRefs.current.reduce((acc, curr) => {
      if (!curr) return acc;
      const computedStyle = window.getComputedStyle(curr);
      const isHidden = computedStyle.display === "none";
      return acc + Number(isHidden);
    }, 0);
    hiddenCount.current = count;
  }, [spanRefs.current.length]);
  const handleRef = useCallback((element, index) => {
    if (!element) spanRefs.current = [...spanRefs.current.slice(0, index), ...spanRefs.current.slice(index + 1)];
    spanRefs.current[index] = element;
  }, []);
  return /* @__PURE__ */ jsx("div", { ref: wrapperRef, className: cn("ui-multiselect", className), onClick: handleClick, children: /* @__PURE__ */ jsxs("div", { className: "ui-multiselect__wrapper", children: [
    values.map(({ label, value }, index) => /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsxs(
      "span",
      {
        ref: (el) => handleRef(el, index),
        "data-tag": "multi-select-value",
        className: bem("ui-multiselect", "tag", { hidden: isOutOfVisibility(index) }),
        children: [
          iconName && /* @__PURE__ */ jsx(Icon, { name: iconName, size: "small" }),
          /* @__PURE__ */ jsx("span", { className: "ui-multiselect__tag-label", children: label }),
          /* @__PURE__ */ jsx(
            Icon,
            {
              name: "cross",
              size: "medium",
              onClick: (event) => {
                event.stopPropagation();
                onRemove(value);
              }
            }
          )
        ]
      },
      value
    ) })),
    !!hiddenCount.current && /* @__PURE__ */ jsx("span", { className: "ui-multiselect__tag-count", children: `+${hiddenCount.current}` }),
    /* @__PURE__ */ jsx(
      "input",
      {
        type: "text",
        ref: inputRef,
        value: inputValue,
        onChange: (e) => onInputChange(e.target.value),
        onKeyDown: handleKeyDown,
        className: "ui-multiselect__input",
        placeholder,
        disabled
      }
    )
  ] }) });
};
const Notification = ({
  type,
  children,
  size = "medium",
  onClose,
  disableItemsCenter = false,
  textAlign = "center"
}) => {
  const name = {
    success: "checkout-circle",
    error: "cross-circle",
    warning: "alert",
    disabled: "cross-circle",
    info: "info"
  };
  return /* @__PURE__ */ jsxs("div", { className: cn("ui-notification row", { "items-center": !disableItemsCenter }, type), children: [
    /* @__PURE__ */ jsx(Icon, { className: "mr-2", name: name[type], size }),
    /* @__PURE__ */ jsx("span", { className: "body-14 ui-notification__content", style: { textAlign }, children }),
    onClose && /* @__PURE__ */ jsx(Button, { variant: "text", onClick: onClose, children: /* @__PURE__ */ jsx(Icon, { name: "cross", size: "medium" }) })
  ] });
};
const Radio = ({
  name,
  dataTag,
  children,
  onChange,
  checked,
  disabled,
  centered,
  role,
  ariaLabel
}) => /* @__PURE__ */ jsxs("label", { className: cn("ui-radio", { disabled, centered, checked }), children: [
  /* @__PURE__ */ jsx(
    "input",
    {
      name,
      "data-tag": dataTag ?? "ui-radio",
      className: "ui-radio-input",
      type: "radio",
      disabled,
      checked,
      "aria-label": ariaLabel,
      role,
      tabIndex: 0,
      onChange
    }
  ),
  /* @__PURE__ */ jsx("span", { className: "ui-radio-label nowrap", children })
] });
const RadioGroup = ({
  name,
  value,
  options,
  onChange,
  disabled,
  renderRadio,
  label,
  direction = "column",
  className,
  dataTag
}) => {
  const renderOption = (option, index) => {
    const radioElement = /* @__PURE__ */ jsx(
      Radio,
      {
        name,
        dataTag: `ui-radio-${option.value}`,
        checked: option.value === value,
        onChange: () => {
          onChange(option.value);
        },
        disabled: disabled ?? option.disabled,
        centered: option.centered,
        role: "radio",
        children: option.label
      },
      String(option.value)
    );
    return renderRadio ? renderRadio(option, radioElement, index) : radioElement;
  };
  return /* @__PURE__ */ jsxs("div", { className: cn("column", bem("ui-radio-group", "container"), className), "data-tag": dataTag, children: [
    label != null && /* @__PURE__ */ jsx("div", { className: bem("ui-radio-group", "label"), children: label }),
    /* @__PURE__ */ jsx("div", { className: `ui-radio-group ${direction}`, children: options.map(renderOption) })
  ] });
};
const ExpandableRadioGroup = (props) => {
  const renderRadio = (option, radio, index) => {
    const lastElement = props.options.length === index + 1;
    return /* @__PURE__ */ jsxs(Fragment$1, { children: [
      radio,
      option.value === props.value && option.content,
      props.divider && !lastElement && /* @__PURE__ */ jsx(Divider, {})
    ] }, option.value.toString());
  };
  return /* @__PURE__ */ jsx(RadioGroup, { ...props, renderRadio });
};
const isTouchEvent = (e) => {
  return "changedTouches" in e;
};
const getFingerPosition = (e) => {
  return isTouchEvent(e) ? e.changedTouches[0].clientX : e.clientX;
};
const Thumb = (props) => {
  const { value, sliderBounds, thumbBounds, sliderRef, onChange } = props;
  const thumbRef = useRef(null);
  const onThumbMove = (e) => {
    const { left, width } = sliderRef.current.getBoundingClientRect();
    const x = getFingerPosition(e);
    const percent = (x - left) / width;
    const value2 = sliderBounds.min + (sliderBounds.max - sliderBounds.min) * percent;
    const clampedValue = clamp(value2, thumbBounds.min, thumbBounds.max);
    onChange(clampedValue);
  };
  const onThumbUp = () => {
    thumbRef.current?.blur();
    document.removeEventListener("mousemove", onThumbMove);
    document.removeEventListener("mouseup", onThumbUp);
    document.removeEventListener("touchmove", onThumbMove);
    document.removeEventListener("touchend", onThumbUp);
  };
  const onMouseDown = (e) => {
    e.preventDefault();
    thumbRef.current?.focus();
    document.addEventListener("mousemove", onThumbMove);
    document.addEventListener("mouseup", onThumbUp);
  };
  const onTouchStart = (e) => {
    e.preventDefault();
    thumbRef.current?.focus();
    document.addEventListener("touchmove", onThumbMove);
    document.addEventListener("touchend", onThumbUp);
  };
  return /* @__PURE__ */ jsx(
    "div",
    {
      ref: thumbRef,
      onMouseDown,
      onTouchStart,
      tabIndex: 0,
      className: "ui-slider__thumb",
      style: { left: `${value}%` }
    }
  );
};
const Slider = (props) => {
  const { value, onChange, min, max, minDistance } = props;
  const sliderRef = useRef(null);
  const { start, size } = useMemo(() => {
    return {
      start: (value[0] - min) / (max - min) * 100,
      size: (value[1] - value[0]) / (max - min) * 100
    };
  }, [max, min, value]);
  const sliderBounds = useMemo(() => ({ min, max }), [min, max]);
  return /* @__PURE__ */ jsxs("div", { className: "ui-slider", ref: sliderRef, children: [
    /* @__PURE__ */ jsx("div", { className: "ui-slider__rail" }),
    /* @__PURE__ */ jsx("div", { className: "ui-slider__track", style: { left: `${start}%`, width: `${size}%` } }),
    /* @__PURE__ */ jsx(
      Thumb,
      {
        sliderRef,
        value: start,
        sliderBounds,
        thumbBounds: { min, max: value[1] - minDistance },
        onChange: (thumb) => onChange([thumb, value[1]])
      }
    ),
    /* @__PURE__ */ jsx(
      Thumb,
      {
        sliderRef,
        value: start + size,
        sliderBounds,
        thumbBounds: { min: value[0] + minDistance, max },
        onChange: (thumb) => onChange([value[0], thumb])
      }
    )
  ] });
};
const Stepper = ({ items, activeItem }) => {
  const activeIndex = useMemo(() => items.findIndex((item) => item.key === activeItem), [items, activeItem]);
  return /* @__PURE__ */ jsx("div", { className: bem("ui-stepper"), children: items.map(({ key, label }, index) => {
    const isBeforeActive = activeIndex > index;
    return /* @__PURE__ */ jsxs(
      "div",
      {
        className: bem("ui-stepper", "item", { active: key === activeItem, ["before-active"]: isBeforeActive }),
        children: [
          /* @__PURE__ */ jsx("div", { className: bem("ui-stepper", "item-index"), children: isBeforeActive ? /* @__PURE__ */ jsx(Icon, { name: "checkout", size: "large" }) : index + 1 }),
          label && /* @__PURE__ */ jsx("div", { className: bem("ui-stepper", "item-label"), children: label }),
          index < items.length - 1 && /* @__PURE__ */ jsx("div", { className: bem("ui-stepper", "item-line") })
        ]
      },
      key
    );
  }) });
};
const Switch = ({ checked = false, onChange, disabled, onBlur }) => {
  return /* @__PURE__ */ jsxs("div", { className: bem("ui-switch", { disabled, checked }), children: [
    /* @__PURE__ */ jsx(
      "input",
      {
        className: "ui-switch__input",
        type: "checkbox",
        checked,
        onClick: () => onChange?.(!checked),
        disabled,
        onBlur
      }
    ),
    /* @__PURE__ */ jsx("div", { className: "ui-switch__thumb" }),
    /* @__PURE__ */ jsx("div", { className: "ui-switch__track" })
  ] });
};
const Tooltip = (props) => {
  const {
    children,
    action = "click",
    hideWhenHovered,
    content,
    disabled,
    popperClassName,
    className,
    onOpenChanged,
    dataTag,
    // Popper props. Maybe we should move them under a separate property later?
    opened,
    position,
    anchorWidth,
    fullWidth,
    maxWidth,
    maxHeight,
    ...rest
  } = props;
  const [isShown, setIsShown] = useState(false);
  const isPopupVisible = opened ?? isShown;
  const [anchorElement, setAnchorElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const hideTimeout = useRef(void 0);
  const handleShownChanged = useCallback(
    (isOpened) => {
      if (disabled || !content) return;
      clearTimeout(hideTimeout.current);
      if (isOpened) {
        setIsShown(isOpened);
        onOpenChanged?.(isOpened);
      } else {
        hideTimeout.current = setTimeout(() => {
          setIsShown(isOpened);
          onOpenChanged?.(isOpened);
        }, 50);
      }
    },
    [disabled, onOpenChanged, content]
  );
  const elements = useMemo(
    () => [{ current: anchorElement }, { current: popperElement }],
    [anchorElement, popperElement]
  );
  const hidePopper = useCallback(() => handleShownChanged(false), [handleShownChanged]);
  useClickOutside(elements, hidePopper);
  const toggleTooltip = () => handleShownChanged(!isPopupVisible);
  const openTooltip = () => handleShownChanged(true);
  const handleShowOnMouseEnter = () => {
    handleShownChanged(true);
  };
  const handleCloseOnMouseLeave = (event) => {
    if (event.relatedTarget === anchorElement || event.relatedTarget === popperElement) return;
    hidePopper();
  };
  const clickCallback = {
    click: toggleTooltip,
    focus: openTooltip
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      "div",
      {
        "data-tag": dataTag,
        ref: setAnchorElement,
        className: cn("ui-tooltip", className),
        onMouseEnter: action === "hover" ? handleShowOnMouseEnter : void 0,
        onMouseLeave: action === "hover" ? handleCloseOnMouseLeave : void 0,
        onClick: clickCallback[action],
        onTouchStart: action === "touch" ? toggleTooltip : void 0,
        onFocus: action === "focus" ? openTooltip : void 0,
        onKeyDown: (event) => onKeyDownEventHandler(event, clickCallback[action]),
        ...rest,
        children
      }
    ),
    content && /* @__PURE__ */ jsx(
      Popper,
      {
        opened: isPopupVisible && !disabled,
        position,
        className: popperClassName,
        anchorWidth,
        onRendered: setPopperElement,
        anchorElement,
        fullWidth,
        maxWidth,
        maxHeight,
        onMouseEnter: action === "hover" && !hideWhenHovered ? handleShowOnMouseEnter : void 0,
        onMouseLeave: action === "hover" && !hideWhenHovered ? handleCloseOnMouseLeave : void 0,
        children: content
      }
    )
  ] });
};
const Tabs = ({
  variant = "primary",
  options,
  onClick,
  value
}) => {
  const [clickedDisabledElement, setClickedDisabledElement] = useState(null);
  const isMobile = useIsMobile();
  const handleOnKeyDown = (event, item) => {
    if (event.key === "Enter" && !item.disabled) onClick(item.value);
  };
  const handleDisabledTabClick = (item) => {
    if (item.disabledTooltip) {
      setClickedDisabledElement(item);
    }
  };
  const headerWrapperClasses = bem("ui-tabs", "header-wrapper", { outlined: variant === "outlined" });
  const labelClasses = (item, disabled) => bem("ui-tabs", `header-label`, {
    active: value === item,
    disabled,
    "mobile-clickable": disabled && isMobile
  });
  return /* @__PURE__ */ jsxs("div", { className: bem("ui-tabs", { [variant]: true }), children: [
    /* @__PURE__ */ jsx("div", { className: "ui-tabs__header row", children: /* @__PURE__ */ jsx("div", { className: headerWrapperClasses, role: "tablist", children: options?.map((item) => {
      const tabElement = /* @__PURE__ */ jsx(
        "div",
        {
          role: "tab",
          tabIndex: item.disabled ? -1 : 0,
          className: labelClasses(item.value, item.disabled),
          onKeyDown: (event) => {
            handleOnKeyDown(event, item);
          },
          onMouseDown: (event) => {
            event.preventDefault();
          },
          onClick: () => {
            if (!item.disabled) {
              onClick(item.value);
            } else if (item.disabledTooltip) {
              handleDisabledTabClick(item);
            }
          },
          children: item.label
        },
        item.value
      );
      if (item.disabled && item.disabledTooltip) {
        if (isMobile) return tabElement;
        return /* @__PURE__ */ jsx(Tooltip, { content: item.disabledTooltip, action: "hover", position: "bottom", children: tabElement }, item.value);
      }
      return tabElement;
    }) }) }),
    isMobile && clickedDisabledElement && clickedDisabledElement.disabledTooltip && /* @__PURE__ */ jsx(
      Modal,
      {
        opened: true,
        onClose: () => setClickedDisabledElement(null),
        title: clickedDisabledElement.disabledTooltipTitle || "",
        maxWidth: "sm",
        children: /* @__PURE__ */ jsx("div", { className: bem("ui-tabs", "mobile-modal"), children: clickedDisabledElement.disabledTooltip })
      }
    )
  ] });
};
const TearDivider = () => /* @__PURE__ */ jsx("div", { className: "tear", children: /* @__PURE__ */ jsx("div", { className: "tear-divider" }) });
const dayInterval = { start: startOfDay(/* @__PURE__ */ new Date()), end: endOfDay(/* @__PURE__ */ new Date()) };
const hoursInterval = eachHourOfInterval(dayInterval);
const TimeSelectorDropdown = ({ onChange, label, options, value, errorMessage }) => {
  const { dateLocale: locale } = useSettings();
  const timeOptions = useMemo(() => hoursInterval.map((hour) => format(hour, "HH:mm", { locale })), [locale]);
  const dropdownItems = useMemo(
    () => options || timeOptions.map((option) => ({ value: option, selectedLabel: option })),
    [options, timeOptions]
  );
  return /* @__PURE__ */ jsx(
    Dropdown,
    {
      className: "time-selector",
      value,
      onChange,
      checkedMark: true,
      label,
      items: dropdownItems,
      errorMessage
    }
  );
};
const TimeSelector = (props) => {
  const {
    time: { textInputEnabled }
  } = useSettings();
  if (textInputEnabled && !props.options) {
    const { onChange, label, value, errorMessage } = props;
    return /* @__PURE__ */ jsx(
      Input,
      {
        className: "time-selector",
        type: "time",
        value,
        label,
        errorMessage,
        onChange,
        required: true,
        ariaLabel: label
      }
    );
  }
  return /* @__PURE__ */ jsx(TimeSelectorDropdown, { ...props });
};
const Currency = ({ currency, value, locale }) => {
  const currencyUtils = useCurrency();
  const formattedValue = currencyUtils.create(value, currency, { locale }).format();
  return /* @__PURE__ */ jsx("span", { children: formattedValue });
};
const Money = ({ fractional, currency, signed, locale }) => {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    signed && fractional >= 0 && "+",
    /* @__PURE__ */ jsx(Currency, { currency, value: fractional, locale })
  ] });
};
const tabletScreenWidth = 1024;
const useIsTablet = () => useScreenIsLess(tabletScreenWidth);
export {
  Accordion,
  Alert,
  Autocomplete,
  Button,
  ButtonGroup,
  Calendar,
  Card,
  Carousel,
  Item$1 as CarouselItem,
  Checkbox,
  CheckboxGroup,
  Currency,
  DatePicker,
  Divider,
  Dropdown,
  ErrorMessage,
  ExpandableRadioGroup,
  FileUpload,
  Icon,
  IconText,
  Input,
  InputLabel,
  LinkButton,
  Loader,
  MediaCarousel,
  Item as MediaCarouselItem,
  Modal,
  Money,
  MultiSelect,
  Notification,
  Option,
  Popper,
  PopupContainerContext,
  Radio,
  RadioGroup,
  SettingsContext,
  Skeleton,
  Slider,
  Stepper,
  SvgIcon,
  Switch,
  Tabs,
  Tag,
  TearDivider,
  TimeSelector,
  Tooltip,
  bem,
  useArrowControls,
  useClickOutside,
  useIsMobile,
  useIsTablet,
  useScreenIsLess
};
//# sourceMappingURL=owl-frontend-ui.js.map
